/**
 * A busy or rate-limited AI provider (Gemini 503 UNAVAILABLE, 429, an
 * overloaded Claude, a timeout) shows one plain sentence in the chat, with
 * Retry and the raw text behind Details.
 *
 * It reaches the panel two ways:
 * - service-core's failover: when every configured model throws, /assistant/chat
 *   answers 200 with the reply "Every configured model failed." followed by one
 *   "• PROVIDER/model — <exception message>" line per attempt, and that message
 *   carries the provider's raw JSON. The reply is stored in the session, so it
 *   also comes back when the chat is reopened.
 * - a failed request: 503/504/529, a 429 that is not our own quota, a timeout.
 */

export const PROVIDER_BUSY_MESSAGE = 'The AI provider is busy right now. Try again in a minute.'

/** service-core AgentOrchestratorService, when the whole failover chain failed. */
const FAILOVER_PREFIX = 'Every configured model failed.'

const BUSY_PATTERNS: RegExp[] = [
  /\b(429|503|504|529)\b/,
  /\bUNAVAILABLE\b/,
  /\bRESOURCE_EXHAUSTED\b/,
  /\bDEADLINE_EXCEEDED\b/,
  /overloaded/i,
  /rate[ _-]?limit/i,
  /too many requests/i,
  /service unavailable/i,
  /gateway time-?out/i,
  /timed out|timeout/i,
  /try again later/i,
]

export function looksLikeBusyProvider(text: string): boolean {
  return BUSY_PATTERNS.some((re) => re.test(text))
}

/**
 * The raw text when an assistant reply is a busy-provider failure, else null.
 *
 * Only service-core's failover reply (every attempt busy) or a bare provider
 * error object count; an ordinary answer that mentions "503" is left alone.
 * A failover where some model failed for another reason (a bad key, no credit)
 * keeps its own text, which says what to fix.
 */
export function providerBusyReplyDetails(reply: string | null | undefined): string | null {
  const text = (reply ?? '').trim()
  if (!text) return null
  if (text.startsWith(FAILOVER_PREFIX)) {
    const attempts = text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.startsWith('•'))
    if (attempts.length === 0) return looksLikeBusyProvider(text) ? text : null
    return attempts.every(looksLikeBusyProvider) ? text : null
  }
  if (/^\{\s*"error"\s*:/.test(text) && looksLikeBusyProvider(text)) return text
  return null
}

type HttpishError = {
  code?: unknown
  message?: unknown
  response?: { status?: number; data?: unknown }
}

/** The raw text when a failed assistant request was a busy provider or a timeout, else null. */
export function providerBusyErrorDetails(error: unknown): string | null {
  const e = (error ?? {}) as HttpishError
  const status = e.response?.status
  const data = e.response?.data as { error?: unknown; message?: unknown } | string | undefined
  // Our own daily/minute quota is a 429 too; it has its own message.
  if (data && typeof data === 'object' && data.error === 'assistant_quota_exceeded') return null

  const message = typeof e.message === 'string' ? e.message : ''
  const body =
    typeof data === 'string'
      ? data
      : data && typeof data === 'object'
        ? typeof data.message === 'string' && data.message.trim()
          ? data.message
          : JSON.stringify(data)
        : ''
  const raw = [status ? `HTTP ${status}` : '', body || message].filter(Boolean).join(' — ')

  const timedOut =
    e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || /timeout|timed out/i.test(message)
  if (timedOut) return raw || 'Request timed out'
  if (status === 429 || status === 503 || status === 504 || status === 529) return raw
  if (status && status >= 500 && looksLikeBusyProvider(body)) return raw
  return null
}
